import type { Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

export interface UserFormState {
  username: string;
  full_name: string;
  password: string;
  role: string;
  employee_id: string;
  primary_company_id: string;
  company_ids: string[];
}

export interface ApiKeyFormState {
  name: string;
}

export interface LicenseFormState {
  company_id: string;
  seats: string;
  expires_at: string;
  notes: string;
}

export interface CustomerOnboardingFormState {
  company_name: string;
  legal_name: string;
  tin: string;
  base_currency: string;
  admin_username: string;
  admin_full_name: string;
  admin_password: string;
  seats: string;
  expires_at: string;
  license_notes: string;
}

export interface CompanyFormState {
  name: string;
  legal_name: string;
  tin: string;
  base_currency: string;
}

export interface CompanyBankingFormState {
  source_bank_id: string;
  source_branch_id: string;
  source_account_name: string;
  source_account_number: string;
  provider_mode: string;
  provider_name: string;
  provider_base_url: string;
  provider_api_key: string;
  routing_policy: string;
  allow_mobile_money: boolean;
  is_active: boolean;
}

export interface AdminCompany {
  id: number | string;
  name: string;
}

export interface AdminBank {
  id: number | string;
  code: string;
  name: string;
  short_name?: string | null;
  swift_code?: string | null;
  supports_interbank: boolean;
}

export interface AdminBankBranch {
  id: number | string;
  bank_id: number | string;
  code: string;
  name: string;
  city?: string | null;
}

export interface AdminEmployee {
  id: number | string;
  full_name: string;
  employee_code: string;
}

export interface AdminUser {
  id: number | string;
  full_name: string;
  username: string;
  role: string;
  company_id?: number | string;
  employee_id?: number | string | null;
  assigned_company_ids?: Array<number | string>;
}

export interface AdminApiKey {
  id: number | string;
  name: string;
  is_active: boolean;
  last_used?: string | null;
}

export interface AdminLicense {
  id: number | string;
  company_id: number | string;
  company_name?: string | null;
  key_hint: string;
  status: string;
  seats: number;
  expires_at?: string | null;
  notes?: string | null;
}

export interface AdminUsersCardProps {
  form: UserFormState;
  setForm: Dispatch<SetStateAction<UserFormState>>;
  editingUserId: number | string | null;
  companies: AdminCompany[];
  employees: AdminEmployee[];
  users: AdminUser[];
  activeCompanyId: number | string | null;
  isSubmitting: boolean;
  canAssignSystemAdminRole: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onResetForm: () => void;
  onStartEditing: (user: AdminUser) => void;
}

export interface AdminCompaniesCardProps {
  form: CompanyFormState;
  setForm: Dispatch<SetStateAction<CompanyFormState>>;
  companies: AdminCompany[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export interface AdminCustomerOnboardingCardProps {
  form: CustomerOnboardingFormState;
  setForm: Dispatch<SetStateAction<CustomerOnboardingFormState>>;
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export interface AdminCompanyBankingCardProps {
  form: CompanyBankingFormState;
  setForm: Dispatch<SetStateAction<CompanyBankingFormState>>;
  banks: AdminBank[];
  bankBranches: AdminBankBranch[];
  activeCompanyName: string;
  providerApiKeyConfigured: boolean;
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export const AdminCompaniesCard: FC<AdminCompaniesCardProps> = ({
  form,
  setForm,
  companies,
  isSubmitting,
  onSubmit,
}) => {
  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>Company management</h2>
      <p className="helper">
        Use this as the AfriHR system-admin workspace to create company instances. Tenant-scoped company-admin isolation is now
        enforced by the backend, and license keys are issued from the license card below.
      </p>

      <div className="split">
        <label>
          Company name
          <input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
        </label>
        <label>
          Legal name
          <input
            value={form.legal_name}
            onChange={(event) => setForm((current) => ({ ...current, legal_name: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          TIN
          <input
            value={form.tin}
            onChange={(event) => setForm((current) => ({ ...current, tin: event.target.value }))}
          />
        </label>
        <label>
          Base currency
          <input
            value={form.base_currency}
            onChange={(event) => setForm((current) => ({ ...current, base_currency: event.target.value.toUpperCase() }))}
          />
        </label>
      </div>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Create company'}
      </button>

      {companies.length ? (
        <ul className="list compact-list">
          {companies.map((company) => (
            <li key={company.id}>
              <div>
                <strong>{company.name}</strong>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
};

export const AdminCompanyBankingCard: FC<AdminCompanyBankingCardProps> = ({
  form,
  setForm,
  banks,
  bankBranches,
  activeCompanyName,
  providerApiKeyConfigured,
  isSubmitting,
  onSubmit,
}) => {
  const selectedBank = banks.find((bank) => String(bank.id) === form.source_bank_id) || null;

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>Company banking</h2>
      <p className="helper">
        Configure the source payroll account for {activeCompanyName || 'the active company'} and define whether payouts are limited to
        same-bank transfers or may use supported interbank rails.
      </p>

      <div className="split">
        <label>
          Source bank
          <select
            value={form.source_bank_id}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                source_bank_id: event.target.value,
                source_branch_id: '',
              }))
            }
          >
            <option value="">Select bank</option>
            {banks.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name} ({bank.code})
              </option>
            ))}
          </select>
        </label>
        <label>
          Source branch
          <select
            value={form.source_branch_id}
            onChange={(event) => setForm((current) => ({ ...current, source_branch_id: event.target.value }))}
            disabled={!form.source_bank_id}
          >
            <option value="">Select branch</option>
            {bankBranches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name} ({branch.code})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="split">
        <label>
          Source account name
          <input
            value={form.source_account_name}
            onChange={(event) => setForm((current) => ({ ...current, source_account_name: event.target.value }))}
          />
        </label>
        <label>
          Source account number
          <input
            value={form.source_account_number}
            onChange={(event) => setForm((current) => ({ ...current, source_account_number: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          Provider mode
          <select
            value={form.provider_mode}
            onChange={(event) => setForm((current) => ({ ...current, provider_mode: event.target.value }))}
          >
            <option value="sandbox">Sandbox</option>
            <option value="generic_http">Generic HTTP connector</option>
            <option value="manual_file">Manual bank file only</option>
          </select>
        </label>
        <label>
          Provider name
          <input
            value={form.provider_name}
            onChange={(event) => setForm((current) => ({ ...current, provider_name: event.target.value }))}
            placeholder="cbe_bulk"
          />
        </label>
      </div>

      <div className="split">
        <label>
          Provider base URL
          <input
            value={form.provider_base_url}
            onChange={(event) => setForm((current) => ({ ...current, provider_base_url: event.target.value }))}
            placeholder="https://bank.example/api"
          />
        </label>
        <label>
          Provider API key
          <input
            type="password"
            value={form.provider_api_key}
            onChange={(event) => setForm((current) => ({ ...current, provider_api_key: event.target.value }))}
            placeholder={providerApiKeyConfigured ? 'Leave blank to keep existing secret' : ''}
          />
        </label>
      </div>

      <div className="split">
        <label>
          Routing policy
          <select
            value={form.routing_policy}
            onChange={(event) => setForm((current) => ({ ...current, routing_policy: event.target.value }))}
          >
            <option value="same_bank_only">Same bank only</option>
            <option value="allow_supported_interbank">Allow supported interbank payouts</option>
          </select>
        </label>
        <label>
          Integration status
          <select
            value={form.is_active ? 'active' : 'inactive'}
            onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.value === 'active' }))}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={form.allow_mobile_money}
          onChange={(event) => setForm((current) => ({ ...current, allow_mobile_money: event.target.checked }))}
        />
        <span>Allow mobile money destinations alongside bank transfers</span>
      </label>

      {providerApiKeyConfigured ? <p className="helper">A provider secret is already stored. Saving without a new key keeps it unchanged.</p> : null}

      <button type="submit" disabled={isSubmitting || !form.source_bank_id || !form.source_account_number.trim()}>
        {isSubmitting ? 'Saving...' : 'Save banking integration'}
      </button>

      <div className="stacked-section">
        <h3>Supported bank directory</h3>
        {banks.length ? (
          <ul className="list compact-list">
            {banks.map((bank) => (
              <li key={bank.id}>
                <div>
                  <strong>{bank.name}</strong>
                  <span>
                    {bank.code}
                    {bank.swift_code ? ` • ${bank.swift_code}` : ''}
                    {bank.supports_interbank ? ' • interbank enabled' : ' • same-bank only'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {selectedBank ? (
          <p className="helper">
            Branches for {selectedBank.name}: {bankBranches.length ? bankBranches.map((branch) => `${branch.name} (${branch.code})`).join(', ') : 'No branches loaded.'}
          </p>
        ) : null}
      </div>
    </form>
  );
};

export const AdminCustomerOnboardingCard: FC<AdminCustomerOnboardingCardProps> = ({
  form,
  setForm,
  isSubmitting,
  onSubmit,
}) => {
  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>Customer onboarding</h2>
      <p className="helper">Create the company, its first company-admin account, and the initial license key in one action.</p>

      <div className="split">
        <label>
          Company name
          <input
            value={form.company_name}
            onChange={(event) => setForm((current) => ({ ...current, company_name: event.target.value }))}
          />
        </label>
        <label>
          Legal name
          <input
            value={form.legal_name}
            onChange={(event) => setForm((current) => ({ ...current, legal_name: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          TIN
          <input
            value={form.tin}
            onChange={(event) => setForm((current) => ({ ...current, tin: event.target.value }))}
          />
        </label>
        <label>
          Base currency
          <input
            value={form.base_currency}
            onChange={(event) => setForm((current) => ({ ...current, base_currency: event.target.value.toUpperCase() }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          Company admin username
          <input
            value={form.admin_username}
            onChange={(event) => setForm((current) => ({ ...current, admin_username: event.target.value }))}
          />
        </label>
        <label>
          Company admin full name
          <input
            value={form.admin_full_name}
            onChange={(event) => setForm((current) => ({ ...current, admin_full_name: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          Company admin password
          <input
            type="password"
            value={form.admin_password}
            onChange={(event) => setForm((current) => ({ ...current, admin_password: event.target.value }))}
          />
        </label>
        <label>
          Seats
          <input
            type="number"
            min="1"
            value={form.seats}
            onChange={(event) => setForm((current) => ({ ...current, seats: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          License expiry
          <input
            type="date"
            value={form.expires_at}
            onChange={(event) => setForm((current) => ({ ...current, expires_at: event.target.value }))}
          />
        </label>
        <label>
          License notes
          <input
            value={form.license_notes}
            onChange={(event) => setForm((current) => ({ ...current, license_notes: event.target.value }))}
          />
        </label>
      </div>

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Create customer workspace'}
      </button>
    </form>
  );
};

export const AdminUsersCard: FC<AdminUsersCardProps> = ({
  form,
  setForm,
  editingUserId,
  companies,
  employees,
  users,
  activeCompanyId,
  isSubmitting,
  canAssignSystemAdminRole,
  onSubmit,
  onResetForm,
  onStartEditing,
}) => {
  const { t } = useTranslation();
  const needsCompanyScope = ['accountant', 'auditor'].includes(form.role);
  const roleLabel = (role: string) => {
    if (role === 'company_admin') return 'Company admin';
    if (role === 'system_admin' || role === 'admin') return 'System admin';
    return role;
  };

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{editingUserId ? 'Edit user account' : t('userAccounts')}</h2>

      <div className="split">
        <label>
          {t('username')}
          <input
            disabled={Boolean(editingUserId)}
            value={form.username}
            onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))}
          />
        </label>
        <label>
          {t('fullName')}
          <input
            value={form.full_name}
            onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))}
          />
        </label>
      </div>

      <div className="split">
        <label>
          {t('password')}
          <input
            type="password"
            placeholder={editingUserId ? 'Leave blank to keep current password' : ''}
            value={form.password}
            onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
          />
        </label>
        <label>
          {t('role')}
          <select
            value={form.role}
            onChange={(event) =>
              setForm((current) => {
                const nextRole = event.target.value;
                if (!['accountant', 'auditor'].includes(nextRole)) {
                  return { ...current, role: nextRole, primary_company_id: '', company_ids: [] };
                }
                return {
                  ...current,
                  role: nextRole,
                  primary_company_id:
                    current.primary_company_id || String(activeCompanyId || companies[0]?.id || ''),
                  company_ids: current.company_ids.length
                    ? current.company_ids
                    : activeCompanyId
                    ? [String(activeCompanyId)]
                    : [],
                };
              })
            }
          >
            <option value="employee">{t('employeeRole')}</option>
            <option value="manager">{t('managerRole')}</option>
            <option value="finance">{t('financeRole')}</option>
            <option value="hr">{t('hrRole')}</option>
            <option value="company_admin">Company admin</option>
            {canAssignSystemAdminRole ? <option value="system_admin">System admin</option> : null}
            <option value="accountant">Accountant</option>
            <option value="auditor">Auditor</option>
          </select>
        </label>
      </div>

      <p className="helper">
        Company admins are scoped to the selected company. Only system admins can create companies, issue license keys, or assign
        system-admin accounts.
      </p>

      <label>
        {t('linkedEmployee')}
        <select
          value={form.employee_id}
          onChange={(event) => setForm((current) => ({ ...current, employee_id: event.target.value }))}
        >
          <option value="">{t('notAvailable')}</option>
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.full_name} ({employee.employee_code})
            </option>
          ))}
        </select>
      </label>

      {needsCompanyScope ? (
        <>
          <label>
            Primary company
            <select
              value={form.primary_company_id}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  primary_company_id: event.target.value,
                  company_ids: current.company_ids.includes(event.target.value)
                    ? current.company_ids
                    : [event.target.value, ...current.company_ids.filter((companyId) => companyId !== event.target.value)],
                }))
              }
            >
              <option value="">{t('notAvailable')}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Assigned companies
            <select
              multiple
              value={form.company_ids}
              onChange={(event) =>
                setForm((current) => {
                  const nextCompanyIds = Array.from(event.target.selectedOptions, (option) => option.value);
                  const nextPrimaryCompanyId = nextCompanyIds.includes(current.primary_company_id)
                    ? current.primary_company_id
                    : nextCompanyIds[0] || '';
                  return {
                    ...current,
                    company_ids: nextCompanyIds,
                    primary_company_id: nextPrimaryCompanyId,
                  };
                })
              }
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('saving') : editingUserId ? 'Save user changes' : t('createUserAccount')}
      </button>
      {editingUserId ? (
        <button type="button" className="secondary-button" onClick={onResetForm}>
          Cancel edit
        </button>
      ) : null}

      {users.length ? (
        <ul className="list compact-list">
          {users.map((user) => (
            <li key={user.id}>
              <div>
                <strong>{user.full_name}</strong>
                <span>
                  {user.username} • {roleLabel(user.role)}
                  {user.employee_id ? ` • ${t('employee')} #${user.employee_id}` : ''}
                  {user.assigned_company_ids?.length
                    ? ` • ${user.assigned_company_ids
                        .map((companyId) => companies.find((company) => company.id === companyId)?.name || `#${companyId}`)
                        .join(', ')}`
                    : ''}
                  {user.role === 'accountant' || user.role === 'auditor'
                    ? ` • primary ${companies.find((company) => company.id === user.company_id)?.name || `#${user.company_id}`}`
                    : ''}
                </span>
              </div>
              <button type="button" className="secondary-button" onClick={() => onStartEditing(user)}>
                Edit
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
};

export interface AdminApiKeysCardProps {
  form: ApiKeyFormState;
  setForm: Dispatch<SetStateAction<ApiKeyFormState>>;
  generatedApiKey: string;
  apiKeys: AdminApiKey[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
}

export interface AdminLicensesCardProps {
  form: LicenseFormState;
  setForm: Dispatch<SetStateAction<LicenseFormState>>;
  companies: AdminCompany[];
  generatedLicenseKey: string;
  licenses: AdminLicense[];
  isSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onLifecycleAction: (license: AdminLicense, action: 'activate' | 'suspend' | 'expire' | 'renew') => void;
}

export const AdminApiKeysCard: FC<AdminApiKeysCardProps> = ({
  form,
  setForm,
  generatedApiKey,
  apiKeys,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t('apiKeys')}</h2>
      <p className="helper">Use these keys for external integrations such as attendance importers, bank payout connectors, or payroll middleware.</p>
      <label>
        {t('reportName')}
        <input
          value={form.name}
          onChange={(event) => setForm({ name: event.target.value })}
        />
      </label>
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('saving') : t('createApiKey')}
      </button>
      {generatedApiKey ? <p className="helper helper-code">{generatedApiKey}</p> : null}
      {apiKeys.length ? (
        <ul className="list compact-list">
          {apiKeys.map((apiKey) => (
            <li key={apiKey.id}>
              <div>
                <strong>{apiKey.name}</strong>
                <span>
                  {apiKey.is_active ? t('active') : t('inactive')} • {apiKey.last_used || t('notAvailable')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>{t('noApiKeys')}</p>
      )}
    </form>
  );
};

export const AdminLicensesCard: FC<AdminLicensesCardProps> = ({
  form,
  setForm,
  companies,
  generatedLicenseKey,
  licenses,
  isSubmitting,
  onSubmit,
  onLifecycleAction,
}) => {
  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>License keys</h2>
      <p className="helper">Issue and track customer license keys at the AfriHR platform level.</p>
      <label>
        Company
        <select
          value={form.company_id}
          onChange={(event) => setForm((current) => ({ ...current, company_id: event.target.value }))}
        >
          <option value="">Select company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
      </label>
      <div className="split">
        <label>
          Seats
          <input
            type="number"
            min="1"
            value={form.seats}
            onChange={(event) => setForm((current) => ({ ...current, seats: event.target.value }))}
          />
        </label>
        <label>
          Expires on
          <input
            type="date"
            value={form.expires_at}
            onChange={(event) => setForm((current) => ({ ...current, expires_at: event.target.value }))}
          />
        </label>
      </div>
      <label>
        Notes
        <input
          value={form.notes}
          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
        />
      </label>
      <button type="submit" disabled={isSubmitting || !form.company_id}>
        {isSubmitting ? 'Saving...' : 'Issue license key'}
      </button>
      {generatedLicenseKey ? <p className="helper helper-code">{generatedLicenseKey}</p> : null}
      {licenses.length ? (
        <ul className="list compact-list">
          {licenses.map((license) => (
            <li key={license.id}>
              <div>
                <strong>{license.company_name || `#${license.company_id}`}</strong>
                <span>
                  {license.status} • {license.seats} seats • key ending {license.key_hint}
                  {license.expires_at ? ` • expires ${license.expires_at}` : ''}
                </span>
              </div>
              <div className="inline-actions">
                <button type="button" className="secondary-button" onClick={() => onLifecycleAction(license, 'activate')}>
                  Activate
                </button>
                <button type="button" className="secondary-button" onClick={() => onLifecycleAction(license, 'suspend')}>
                  Suspend
                </button>
                <button type="button" className="secondary-button" onClick={() => onLifecycleAction(license, 'expire')}>
                  Expire
                </button>
                <button type="button" className="secondary-button" onClick={() => onLifecycleAction(license, 'renew')}>
                  Renew 1 year
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="helper">No license keys issued yet.</p>
      )}
    </form>
  );
};
